import { useEffect, useState, type FormEvent } from 'react';
import { track } from '../lib/analytics';
import { api, ApiError } from '../lib/api';
import { Button } from './Button';

interface DigestStatus {
  available: boolean;
  status: 'none' | 'pending' | 'active';
  email: string | null;
}

/**
 * Weekly email (Monday: your week, your groups' tables, specials). Double opt-in on the server;
 * hidden entirely until email sending is set up there, and in offline/local builds.
 */
export function DigestSettings() {
  const [state, setState] = useState<DigestStatus | null>(null);
  const [email, setEmail] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    api<DigestStatus>('/digest').then(
      (s) => alive && setState(s),
      () => alive && setState(null),
    );
    return () => {
      alive = false;
    };
  }, []);

  if (!state?.available) return null;

  const subscribe = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setBusy(true);
    setNote(null);
    try {
      await api('/digest/subscribe', { method: 'POST', body: { email } });
      setState({ ...state, status: 'pending', email: null });
      setNote('Check your inbox and tap the link to confirm.');
      track('digest_subscribed');
    } catch (err) {
      setNote(err instanceof ApiError ? err.message : 'That didn’t work. Try again later.');
    } finally {
      setBusy(false);
    }
  };

  const off = async (): Promise<void> => {
    setBusy(true);
    try {
      await api('/digest/unsubscribe-me', { method: 'POST', body: {} });
      setState({ ...state, status: 'none', email: null });
      setNote('Weekly email off.');
      track('digest_unsubscribed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <fieldset className="mb-5 rounded-xl bg-white/5 p-3" data-testid="digest-settings">
      <legend className="px-1 text-sm text-white/70">Weekly email</legend>
      <p className="text-xs text-white/50">
        Every Monday: your week’s scores, your groups’ tables and any special rounds. Unsubscribe in
        one click.
      </p>
      {state.status === 'active' ? (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
          <span>On · {state.email}</span>
          <Button size="sm" variant="ghost" disabled={busy} onClick={() => void off()}>
            Turn off
          </Button>
        </div>
      ) : (
        <form onSubmit={(e) => void subscribe(e)} className="mt-2 flex flex-wrap gap-2">
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            aria-label="Email address"
            className="min-h-10 min-w-0 flex-1 rounded-lg bg-white/10 px-3 text-sm text-white placeholder:text-white/40"
          />
          <Button size="sm" variant="secondary" type="submit" disabled={busy}>
            {state.status === 'pending' ? 'Resend' : 'Sign me up'}
          </Button>
        </form>
      )}
      {(note ?? (state.status === 'pending' ? 'Waiting for you to confirm by email.' : null)) && (
        <p role="status" className="mt-1 text-xs text-white/70">
          {note ?? 'Waiting for you to confirm by email.'}
        </p>
      )}
    </fieldset>
  );
}
